import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import * as THREE from 'three';
import './LandingPage.css';
import { Link } from 'react-router-dom';
window.THREE = THREE; // eslint-disable-line no-unused-vars

function LandingPage() {
  const technologyRef = useRef(null); // Reference for the Vanta background
  const vantaEffect = useRef(null); // Reference to store the Vanta effect for cleanup

  useEffect(() => {
    const loadVanta = async () => {
      await import("three"); // Import three.js dynamically
      const VANTA = await import("vanta/dist/vanta.dots.min"); // Restore original dots effect

      if (!vantaEffect.current && technologyRef.current) {
        vantaEffect.current = VANTA.default({
          el: technologyRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 250.00, // Increased from original
          minWidth: 250.00, // Increased from original
          scale: 1.20, // Increased scale for better coverage
          scaleMobile: 1.20,
          backgroundColor: 0x011220, // Original dark background color
          color: 0x90E0E0, // Original bright cyan dots color
          color2: 0x2f0fd7, // Original secondary color for dots
          size: 4, // Increased size of dots
          spacing: 20.0, // Adjusted spacing between dots
          showLines: false // Keep original setting
        });
      }
    };

    loadVanta();

    return () => {
      // Cleanup function with a check to ensure the node exists before destroying
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
        vantaEffect.current = null; // Reset the effect reference
      }
    };
  }, [technologyRef]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-fade-in');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Simplified card scroll with just 3 cards
  useEffect(() => {
    // Only run on mobile
    if (window.innerWidth <= 768) {
      const cardsContainer = document.getElementById('cards-container');
      const indicators = document.querySelectorAll('.card-indicator');
      const cards = document.querySelectorAll('.card');
      
      if (!cardsContainer || cards.length === 0) return;
      
      // Remove any existing clones
      document.querySelectorAll('.card-clone').forEach(clone => clone.remove());
      
      // Calculate dimensions
      const cardWidth = cardsContainer.offsetWidth * 0.65;
      const gap = 15;
      const totalCardWidth = cardWidth + gap;
      
      // Update active indicator based on visible card
      function updateIndicator(index) {
        // Ensure index is within bounds
        if (index < 0) index = 0;
        if (index >= indicators.length) index = indicators.length - 1;
        
        indicators.forEach((indicator, i) => {
          indicator.classList.toggle('active', i === index);
        });
      }
      
      // Handle scroll event
      function handleScroll() {
        // Calculate which card is currently most visible
        const scrollPosition = cardsContainer.scrollLeft;
        const cardIndex = Math.round(scrollPosition / totalCardWidth);
        
        // Update indicators
        updateIndicator(cardIndex);
      }
      
      // Set up indicator clicks
      indicators.forEach((indicator, i) => {
        indicator.addEventListener('click', () => {
          // Calculate position
          const targetScroll = i * totalCardWidth;
          
          // Scroll to position
          cardsContainer.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
          });
          
          // Update indicators
          updateIndicator(i);
        });
      });
      
      // Listen for scroll events
      cardsContainer.addEventListener('scroll', handleScroll);
      
      // Initialize - show first card as active
      updateIndicator(0);
      
      // Clean up
      return () => {
        cardsContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  return (
    <div id="landing-page">
      <Helmet>
        <title>NoetherTech | Breakthrough Neuropsychiatric Technology</title>
      </Helmet>
      {/* Title Section */}
      <div id="title-section">
        <video playsInline autoPlay muted loop id="title-video">
          <source src="//dv4tl7yyk1zlp.cloudfront.net/635d95ea-c9c3-4b0f-ad86-4d668306526e/e00fa965-1ac4-45c2-8157-7d496c50f28a.mp4" type="video/mp4" />
        </video>
        <div id="title-overlay"></div>
        {/* <div id="title-overlay"></div> */}
        <div id="title-logo-overlay"></div>
        <div id="title-text-container">
          <h1 id="title-main-title">
            <span className="title-part-1">BREAKTHROUGH</span><br/>
            <span className="title-part-2">NEUROPSYCHIATRIC</span><br/>
            <span className="title-part-3">TECHNOLOGY</span>
          </h1>
          {/* <p id="title-subtext">
            ADVANCED NEUROMODULATION FOR OPTIMIZED BRAIN FUNCTION
          </p> */}
          <p id="title-slogan">
            We don't solve mental health problems—we make them more solvable
          </p>
        </div>
        <div id="title-gradient-overlay"></div> 
      </div>

      {/* Technology Section with Vanta Background */}
      <div id="technology-section" ref={technologyRef}>
        <div className="technology-graphics">
          <img src="//dv4tl7yyk1zlp.cloudfront.net/635d95ea-c9c3-4b0f-ad86-4d668306526e/e78243e8-0479-475a-afb9-b6add0d49014.png" alt="Brain Technology" />
        </div>
        <div className="technology-body">
          <div id="technology-overlay-blob"></div>
          <span className="technology-section-number">01. Our Technology</span>
          <h2 className="technology-title animate-fade-in">A Platform to Restore Brain Health</h2>
          <p className="animate-fade-in delay-1">NoetherTech introduces EMBP™ (Electromagnetic Brain Pulsing) Neural Modulation Therapy, a revolutionary approach to treating neurological and psychiatric disorders through personalized neuromodulation therapy.</p>
          <p className="animate-fade-in delay-2">By combining advanced neural analysis with precise electromagnetic stimulation, we optimize brain function and restore neurological health.</p>
          {/* <p>We analyze neural information and utilize our Electromagnetic Brain Pulsing (EMBP™) neural modulation therapy to optimize brain health and restore functionality.</p>
          <p>Our combined expertise in neuroscience, innovative machine learning analysis, and cutting-edge use of EMBP™ neuro-stimulation has actualized in our patented, personalized, and non-invasive neuromodulation treatment.</p> */}
          
          <a href="technology" className="button animate-fade-in delay-3">OUR TECHNOLOGY</a>
        </div>
      </div>

      {/* Technology Cards with indicators */}
      <div className="features-container">
        <ul className="cards-container" id="cards-container">
          <li className="card">
            <h3 className="animate-fade-in">Non-Invasive</h3>
            <p className="animate-fade-in">No surgery, anesthesia, or sedation, our therapy offers a comfortable and pain-free experience.</p>
          </li>
          <li className="card">
            <h3 className="animate-fade-in">Drug Free</h3>
            <p className="animate-fade-in">Our treatment is free from medication. EMBP™ uses targeted electromagnetic pulses to naturally retrain brain activity.</p>
          </li>
          <li className="card">
            <h3 className="animate-fade-in">CNS Targeted</h3>
            <p className="animate-fade-in">Our technology specifically focuses on your central nervous system, delivering customized treatment based on quantitative.</p>
          </li>
        </ul>
        <div className="card-indicators">
          <span className="card-indicator active"></span>
          <span className="card-indicator"></span>
          <span className="card-indicator"></span>
        </div>
      </div>

      {/* History Section */}
      <div id="history-section">
        <div className="history-body">
          <span className="history-section-number">02. Our History</span>
          <h2 className="history-title animate-fade-in">Why EMBP™?</h2>
          <p className="animate-fade-in delay-1">EMBP™ evolved from three decades of groundbreaking research in brain-wave guided transcranial magnetic stimulation by Dr. Yi Jin, MD. Developed and patented by NoetherTech, This proprietary technology builds upon proven neuroscientific principles and represents the next evolution in neuromodulation therapy.</p>
          <a href="history" className="button animate-fade-in delay-2">OUR JOURNEY</a>
        </div>
        <div className="history-graphics">
          <img src="//dv4tl7yyk1zlp.cloudfront.net/635d95ea-c9c3-4b0f-ad86-4d668306526e/bf6a3652-09a9-47fe-a879-1575000e47a6.png" alt="Brain visualization" />
          <div className="history-dots">
            {[...Array(196)].map((_, i) => (
              <div key={i} className="history-dot"></div>
            ))}
          </div>
        </div>
      </div>

      {/* About Us Section */}
      <div id="about-us-section">
        <div className="about-us-card">
          <div className="about-us-header">
            <h2 className="animate-fade-in">Who We Are</h2>
            <Link to="/team" className="button animate-fade-in delay-1 desktop-only-button">OUR TEAM</Link>
          </div>
          <div className="about-us-body">
            <p className="animate-fade-in delay-1">NoetherTech emerges from a legacy of neurological innovation, brought by the pioneers behind the Newport Brain Research Laboratory (NBRL), Brain Treatment Center (BTC) and the creators of the MRT and MeRT technologies currently used by WaveNeuro™.</p>
          </div>
          {/* Button for mobile only - at bottom of card */}
          <Link to="/team" className="button animate-fade-in delay-1 mobile-only-button"></Link>
        </div>
      </div>

      {/* Science Section - restructured */}
      <section id="science-section">
        <div className="science-background-rectangle"></div>
        
        <div className="science-card">
          <div className="science-graphics">
            <img src="//dv4tl7yyk1zlp.cloudfront.net/635d95ea-c9c3-4b0f-ad86-4d668306526e/c372bcab-40dc-4f6c-8402-5920b4efdac9.jpg" alt="Emmy Noether" />
          </div>
          {/* Mobile-only section number */}
          <span className="science-section-number">03. The Science</span>
          <div className="science-body">
            <h2 className="science-title animate-fade-in">In Honor of Mathematician Amalie Emmy Noether</h2>
            <p className="animate-fade-in delay-1">Our name honors Amalie Emmy Noether, a revolutionary mathematician whose theorem fundamentally transformed theoretical physics.</p>
            <p className="animate-fade-in delay-2">Like Noether's work in revealing the relationship between symmetries and conservation laws, our technology seeks to understand and optimize the brain's natural patterns and rhythms.</p>
            <a href="science" className="button animate-fade-in delay-3 desktop-only-button">THE SCIENCE</a>
            <a href="science" className="button animate-fade-in delay-3 mobile-only-button" aria-label="Learn about the science"></a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;

